import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import { useCurrentAuthRequest } from 'Src/feature/auth-request/useCurrentAuthRequest';
import { useCardReader } from 'Src/feature/card-reader/useCardReader';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { useToast } from 'Src/feature/toasts/useToast';

import { useAuthRequest } from './useAuthRequest';

class ApprovalForm {
  pin?: string = undefined;
  swipe?: string = undefined;
  constructor(data: Partial<ApprovalForm> = {}) {
    Object.assign(this, data);
  }
}

const { Form, Field } = getTypedFormComponents<ApprovalForm>();

const sectionId = 'useAuthRequestUIBlocker';

const useAuthRequestUIBlocker = () => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const cardReader = useCardReader();
  const { currentRequest, setCurrentRequest } = useCurrentAuthRequest();
  const toast = useToast();
  const answerAuth = useAuthRequest.mutate.answer();
  const isOpen = !!currentRequest;

  const form = useForm<ApprovalForm>({
    defaultValues: { ...new ApprovalForm() },
    schema: object().shape({
      pin: string().
        label(t('labels.pin')).
        optional().
        min(3),
      swipe: string().
        optional().
        min(3).
        test('invalid-swipe', t('payment.info.pleaseSwipeCard'),
          (value) => !value?.toLowerCase().includes('invalid')
        ),
    }),
  });

  const onCancel = async () => {
    answerAuth.mutate({
      authRequestId: currentRequest?.id as number,
      outcome: 'Canceled'
    }, {
      onError: (error) => {
        toast.show.serviceError(error, { id: `${sectionId}.authRequest-cancel`, title: t('authRequest.errors.cancel') });
        setCurrentRequest(undefined);
      }
    });
  };

  const onSave = useCallback(async (options: { onAfterError: () => void }) => {
    await form.action.submitIfValid(async ({ values }) => {
      answerAuth.mutate({
        authRequestId: currentRequest?.id as number,
        outcome: 'Approved',
        pin: values.pin,
        cardCode: values.swipe,
      }, {
        onError: (err) => {
          toast.show.serviceError(err, { id:`${sectionId}.authRequest-approved`, title: t('authRequest.errors.approve') });
          setCurrentRequest(undefined);
          options.onAfterError();
        }
      });
    });
  }, [answerAuth, currentRequest?.id, form.action, setCurrentRequest, t, toast.show]);

  const reInitCardReader = useCallback(() => {
    cardReader.init({
      mode: 'msr',
      amount: 0,
      onSuccess: (read) => {
        if (!read || !read.pax) {
          log.error('cardReader.onSuccess()', 'no pax data received');
          return false;
        }
        // In the case the card reader fails to read the card swiped
        if (cardReader.is.employeeSwipeInvalid(read.pax)) {
          void read.reInit();
        }
        form.action.setValue('pin', undefined, { shouldValidate: true, shouldTouch: true, shouldDirty: true });
        form.action.setValue('swipe', read.pax.employeeSwipeCode, { shouldValidate: true, shouldTouch: true, shouldDirty: true });
        void onSave({ onAfterError: read.reInit });
      },
      onTimeout: () => true,
      onError: () => true,
    });
  }, [cardReader, form.action, log, onSave]);

  useEffect(() => {
    if (isOpen) {
      reInitCardReader();
    }

    return () => {
      if (isOpen) {
        void cardReader.dispose();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return {
    form,
    onSave: () => onSave({ onAfterError: reInitCardReader }),
    onCancel,
    isOpen,
    isCardReaderConnected: cardReader.state.isConnected,
  };
};

export { Field, Form, useAuthRequestUIBlocker };
